import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import './BuyerOfferForm.css';

function BuyerOfferForm({ listingId: propListingId, onOfferSubmitted, onClose }) {
  const navigate = useNavigate();
  const { id: paramListingId } = useParams();
  const listingId = propListingId || paramListingId;

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const [formData, setFormData] = useState({
    // Offer Terms
    offerAmount: '',
    earnestMoney: '',
    optionFee: '100',
    optionDays: '10',
    closingDate: '',
    offerExpiration: '',

    // Financing
    financingType: 'conventional',
    downPaymentPercent: '20',
    preApprovalLetter: null,
    proofOfFunds: null,

    // Buyer Info
    buyerName: '',
    buyerEmail: '',
    buyerPhone: '',

    // Buyer Agent (optional)
    hasBuyerAgent: false,
    buyerAgentName: '',
    buyerAgentEmail: '',
    buyerAgentPhone: '',
    buyerAgentLicense: '',
    buyerAgentBrokerage: '',

    // Additional Terms
    surveyRequired: true,
    homeWarrantyRequired: false,
    homeWarrantyAmount: '500',
    contingencies: '',
    specialProvisions: '',
    message: ''
  });

  useEffect(() => {
    fetchListing();
    prefillBuyerInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId]);

  const fetchListing = async () => {
    try {
      const response = await axios.get(`/api/listings/${listingId}`);
      setListing(response.data);
      
      // Pre-fill suggested values based on listing
      setFormData(prev => ({
        ...prev,
        offerAmount: response.data.price?.toString() || '',
        earnestMoney: Math.round(response.data.price * 0.01).toString(), // 1% earnest money
        closingDate: getDefaultClosingDate()
      }));
    } catch (err) {
      setError('Failed to load listing details');
    } finally {
      setLoading(false);
    }
  };

  const prefillBuyerInfo = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user) {
      setFormData(prev => ({
        ...prev,
        buyerName: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
        buyerEmail: user.email || '',
        buyerPhone: user.phone || ''
      }));
    }
  };

  const getDefaultClosingDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 30); // 30 days from now
    return date.toISOString().split('T')[0];
  };

  const getDefaultExpirationDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 3); // 3 days from now
    return date.toISOString().split('T')[0];
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'file' ? files[0] : value
    }));
  };

  const formatCurrency = (value) => {
    const num = parseInt(value.replace(/[^0-9]/g, ''));
    if (isNaN(num)) return '';
    return num.toLocaleString();
  };

  const handleCurrencyChange = (e) => {
    const { name, value } = e.target;
    const numericValue = value.replace(/[^0-9]/g, '');
    setFormData(prev => ({ ...prev, [name]: numericValue }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1: // Offer Terms
        if (!formData.offerAmount || parseInt(formData.offerAmount) <= 0) {
          setError('Please enter a valid offer amount');
          return false;
        }
        if (!formData.earnestMoney || parseInt(formData.earnestMoney) <= 0) {
          setError('Please enter earnest money amount');
          return false;
        }
        if (!formData.closingDate) {
          setError('Please select a closing date');
          return false;
        }
        break;
      case 2: // Financing
        if (formData.financingType !== 'cash' && !formData.downPaymentPercent) {
          setError('Please enter down payment percentage');
          return false;
        }
        break;
      case 3: // Buyer Info
        if (!formData.buyerName.trim()) {
          setError('Please enter buyer name');
          return false;
        }
        if (!formData.buyerEmail.trim()) {
          setError('Please enter buyer email');
          return false;
        }
        if (!formData.buyerPhone.trim()) {
          setError('Please enter buyer phone');
          return false;
        }
        break;
      default:
        break;
    }
    setError('');
    return true;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(currentStep)) return;

    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      // Create form data for file upload
      const submitData = new FormData();
      
      // Add all form fields
      submitData.append('listing_id', listingId);
      submitData.append('offer_amount', formData.offerAmount);
      submitData.append('earnest_money', formData.earnestMoney);
      submitData.append('option_fee', formData.optionFee);
      submitData.append('option_days', formData.optionDays);
      submitData.append('closing_date', formData.closingDate);
      submitData.append('offer_expiration', formData.offerExpiration || getDefaultExpirationDate());
      submitData.append('financing_type', formData.financingType);
      submitData.append('down_payment_percent', formData.downPaymentPercent);
      submitData.append('buyer_name', formData.buyerName);
      submitData.append('buyer_email', formData.buyerEmail);
      submitData.append('buyer_phone', formData.buyerPhone);
      submitData.append('has_buyer_agent', formData.hasBuyerAgent);
      
      if (formData.hasBuyerAgent) {
        submitData.append('buyer_agent_name', formData.buyerAgentName);
        submitData.append('buyer_agent_email', formData.buyerAgentEmail);
        submitData.append('buyer_agent_phone', formData.buyerAgentPhone);
        submitData.append('buyer_agent_license', formData.buyerAgentLicense);
        submitData.append('buyer_agent_brokerage', formData.buyerAgentBrokerage);
      }
      
      submitData.append('survey_required', formData.surveyRequired);
      submitData.append('home_warranty_required', formData.homeWarrantyRequired);
      if (formData.homeWarrantyRequired) {
        submitData.append('home_warranty_amount', formData.homeWarrantyAmount);
      }
      submitData.append('contingencies', formData.contingencies);
      submitData.append('special_provisions', formData.specialProvisions);
      submitData.append('message', formData.message);
      
      // Add files if present
      if (formData.preApprovalLetter) {
        submitData.append('pre_approval_letter', formData.preApprovalLetter);
      }
      if (formData.proofOfFunds) {
        submitData.append('proof_of_funds', formData.proofOfFunds);
      }

      const response = await axios.post('/api/offers/submit', submitData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setSuccess(true);
      
      if (onOfferSubmitted) {
        onOfferSubmitted(response.data);
      }

      // Redirect after 3 seconds
      setTimeout(() => {
        if (onClose) {
          onClose();
        } else {
          navigate('/buyer-portal');
        }
      }, 3000);

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit offer. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="offer-form-loading">
        <div className="spinner"></div>
        <p>Loading listing details...</p>
      </div>
    );
  }

  if (success) {
    return (
      <div className="offer-form-success">
        <div className="success-icon">✓</div>
        <h2>Offer Submitted!</h2>
        <p>Your offer of <strong>${parseInt(formData.offerAmount).toLocaleString()}</strong> has been sent to the seller.</p>
        <p className="success-note">You'll receive a notification when the seller responds.</p>
      </div>
    );
  }

  return (
    <div className="buyer-offer-form">
      <div className="offer-form-header">
        <h2>Make an Offer</h2>
        {listing && (
          <div className="listing-summary">
            <p className="listing-address">{listing.address}, {listing.city}, {listing.state}</p>
            <p className="listing-price">Asking: ${listing.price?.toLocaleString()}</p>
          </div>
        )}
      </div>

      {/* Progress Steps */}
      <div className="offer-steps">
        {['Offer Terms', 'Financing', 'Your Info', 'Review'].map((step, idx) => (
          <div 
            key={idx} 
            className={`offer-step ${currentStep === idx + 1 ? 'active' : ''} ${currentStep > idx + 1 ? 'completed' : ''}`}
          >
            <span className="step-number">{idx + 1}</span>
            <span className="step-name">{step}</span>
          </div>
        ))}
      </div>

      {error && (
        <div className="offer-error">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Step 1: Offer Terms */}
        {currentStep === 1 && (
          <div className="offer-form-section">
            <h3>Offer Terms</h3>
            
            <div className="form-group">
              <label>Offer Amount *</label>
              <div className="input-with-prefix">
                <span>$</span>
                <input
                  type="text"
                  name="offerAmount"
                  value={formatCurrency(formData.offerAmount)}
                  onChange={handleCurrencyChange}
                  placeholder="0"
                />
              </div>
              {listing && formData.offerAmount && (
                <p className="input-hint">
                  {((parseInt(formData.offerAmount) / listing.price) * 100).toFixed(1)}% of asking price
                </p>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Earnest Money *</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="text"
                    name="earnestMoney"
                    value={formatCurrency(formData.earnestMoney)}
                    onChange={handleCurrencyChange}
                    placeholder="0"
                  />
                </div>
                <p className="input-hint">Typically 1-2% of offer price</p>
              </div>

              <div className="form-group">
                <label>Option Fee</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="text"
                    name="optionFee"
                    value={formatCurrency(formData.optionFee)}
                    onChange={handleCurrencyChange}
                    placeholder="100"
                  />
                </div>
                <p className="input-hint">Paid for option period</p>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Option Period (Days)</label>
                <input
                  type="number"
                  name="optionDays"
                  value={formData.optionDays}
                  onChange={handleChange}
                  min="1"
                  max="30"
                />
                <p className="input-hint">Typically 7-14 days for inspections</p>
              </div>

              <div className="form-group">
                <label>Closing Date *</label>
                <input
                  type="date"
                  name="closingDate"
                  value={formData.closingDate}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Offer Expiration</label>
              <input
                type="date"
                name="offerExpiration"
                value={formData.offerExpiration}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]}
              />
              <p className="input-hint">When should this offer expire? (Default: 3 days)</p>
            </div>
          </div>
        )}

        {/* Step 2: Financing */}
        {currentStep === 2 && (
          <div className="offer-form-section">
            <h3>Financing</h3>

            <div className="form-group">
              <label>Financing Type *</label>
              <div className="financing-options">
                {[
                  { value: 'cash', label: 'Cash', icon: '💵' },
                  { value: 'conventional', label: 'Conventional', icon: '🏦' },
                  { value: 'fha', label: 'FHA', icon: '🏛️' },
                  { value: 'va', label: 'VA', icon: '🎖️' },
                  { value: 'usda', label: 'USDA', icon: '🌾' },
                  { value: 'seller_finance', label: 'Seller Finance', icon: '🤝' }
                ].map(option => (
                  <div 
                    key={option.value}
                    className={`financing-option ${formData.financingType === option.value ? 'selected' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, financingType: option.value }))}
                  >
                    <span className="financing-icon">{option.icon}</span>
                    <span className="financing-label">{option.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {formData.financingType !== 'cash' && (
              <div className="form-group">
                <label>Down Payment %</label>
                <div className="input-with-suffix">
                  <input
                    type="number"
                    name="downPaymentPercent"
                    value={formData.downPaymentPercent}
                    onChange={handleChange}
                    min="0"
                    max="100"
                  />
                  <span>%</span>
                </div>
                {formData.offerAmount && formData.downPaymentPercent && (
                  <p className="input-hint">
                    ${Math.round(parseInt(formData.offerAmount) * (parseFloat(formData.downPaymentPercent) / 100)).toLocaleString()} down payment
                  </p>
                )}
              </div>
            )}

            <div className="form-group">
              <label>
                {formData.financingType === 'cash' ? 'Proof of Funds' : 'Pre-Approval Letter'}
              </label>
              <input
                type="file"
                name={formData.financingType === 'cash' ? 'proofOfFunds' : 'preApprovalLetter'}
                onChange={handleChange}
                accept=".pdf,.jpg,.jpeg,.png"
              />
              <p className="input-hint">
                {formData.financingType === 'cash' 
                  ? 'Upload bank statement or proof of funds (recommended)'
                  : 'Upload your mortgage pre-approval letter (recommended)'}
              </p>
            </div>
          </div>
        )}

        {/* Step 3: Buyer Info */}
        {currentStep === 3 && (
          <div className="offer-form-section">
            <h3>Your Information</h3>

            <div className="form-group">
              <label>Full Legal Name *</label>
              <input
                type="text"
                name="buyerName"
                value={formData.buyerName}
                onChange={handleChange}
                placeholder="Enter your full legal name"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="buyerEmail"
                  value={formData.buyerEmail}
                  onChange={handleChange}
                  placeholder="your@email.com"
                />
              </div>

              <div className="form-group">
                <label>Phone *</label>
                <input
                  type="tel"
                  name="buyerPhone"
                  value={formData.buyerPhone}
                  onChange={handleChange}
                  placeholder="(555) 555-5555"
                />
              </div>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="hasBuyerAgent"
                  checked={formData.hasBuyerAgent}
                  onChange={handleChange}
                />
                I am working with a buyer's agent
              </label>
            </div>

            {formData.hasBuyerAgent && (
              <div className="agent-info-section">
                <h4>Buyer's Agent Information</h4>
                
                <div className="form-group">
                  <label>Agent Name</label>
                  <input
                    type="text"
                    name="buyerAgentName"
                    value={formData.buyerAgentName}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Agent Email</label>
                    <input
                      type="email"
                      name="buyerAgentEmail"
                      value={formData.buyerAgentEmail}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Agent Phone</label>
                    <input
                      type="tel"
                      name="buyerAgentPhone"
                      value={formData.buyerAgentPhone}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>License Number</label>
                    <input
                      type="text"
                      name="buyerAgentLicense"
                      value={formData.buyerAgentLicense}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Brokerage</label>
                    <input
                      type="text"
                      name="buyerAgentBrokerage"
                      value={formData.buyerAgentBrokerage}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 4: Review */}
        {currentStep === 4 && (
          <div className="offer-form-section">
            <h3>Review Your Offer</h3>

            <div className="review-section">
              <h4>Offer Terms</h4>
              <div className="review-grid">
                <div className="review-item">
                  <span className="review-label">Offer Amount</span>
                  <span className="review-value">${parseInt(formData.offerAmount).toLocaleString()}</span>
                </div>
                <div className="review-item">
                  <span className="review-label">Earnest Money</span>
                  <span className="review-value">${parseInt(formData.earnestMoney).toLocaleString()}</span>
                </div>
                <div className="review-item">
                  <span className="review-label">Option Fee</span>
                  <span className="review-value">${parseInt(formData.optionFee).toLocaleString()}</span>
                </div>
                <div className="review-item">
                  <span className="review-label">Option Period</span>
                  <span className="review-value">{formData.optionDays} days</span>
                </div>
                <div className="review-item">
                  <span className="review-label">Closing Date</span>
                  <span className="review-value">{new Date(formData.closingDate).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            <div className="review-section">
              <h4>Financing</h4>
              <div className="review-grid">
                <div className="review-item">
                  <span className="review-label">Type</span>
                  <span className="review-value" style={{ textTransform: 'capitalize' }}>
                    {formData.financingType.replace('_', ' ')}
                  </span>
                </div>
                {formData.financingType !== 'cash' && (
                  <div className="review-item">
                    <span className="review-label">Down Payment</span>
                    <span className="review-value">{formData.downPaymentPercent}%</span>
                  </div>
                )}
              </div>
            </div>

            <div className="review-section">
              <h4>Buyer</h4>
              <div className="review-grid">
                <div className="review-item">
                  <span className="review-label">Name</span>
                  <span className="review-value">{formData.buyerName}</span>
                </div>
                <div className="review-item">
                  <span className="review-label">Email</span>
                  <span className="review-value">{formData.buyerEmail}</span>
                </div>
                <div className="review-item">
                  <span className="review-label">Phone</span>
                  <span className="review-value">{formData.buyerPhone}</span>
                </div>
                {formData.hasBuyerAgent && (
                  <div className="review-item">
                    <span className="review-label">Agent</span>
                    <span className="review-value">{formData.buyerAgentName}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Additional Terms or Contingencies</label>
              <textarea
                name="contingencies"
                value={formData.contingencies}
                onChange={handleChange}
                placeholder="Any additional contingencies (inspection, appraisal, etc.)"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Special Provisions</label>
              <textarea
                name="specialProvisions"
                value={formData.specialProvisions}
                onChange={handleChange}
                placeholder="Any special provisions or requests"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Message to Seller (Optional)</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Introduce yourself or explain why you love this home..."
                rows={3}
              />
            </div>

            <div className="disclaimer-box">
              <p><strong>By submitting this offer, you acknowledge:</strong></p>
              <ul>
                <li>This is a non-binding offer until a contract is fully executed by all parties</li>
                <li>You will need to sign the official TREC contract if your offer is accepted</li>
                <li>Earnest money will be due within 3 days of contract execution</li>
                <li>You have the financial ability to complete this purchase</li>
              </ul>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="offer-form-actions">
          {currentStep > 1 && (
            <button type="button" className="btn-back" onClick={prevStep}>
              Back
            </button>
          )}
          
          {onClose && (
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
          )}

          {currentStep < 4 ? (
            <button type="button" className="btn-next" onClick={nextStep}>
              Continue
            </button>
          ) : (
            <button type="submit" className="btn-submit" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Offer'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

export default BuyerOfferForm;
